import { ReactNode, RefObject } from "react";
import { match, P } from "ts-pattern";
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from "@silo/ui";
import { MarketsSort, MarketsSortField, SortBy } from "@silo/types";
import { sortButton } from "./shared";

interface AprSortHeaderProps {
  labelContent: ReactNode;
  labelWidth: string;
  labelRef: RefObject<HTMLDivElement>;
  depositField: MarketsSortField;
  debtField: MarketsSortField;
  marketsSort: MarketsSort;
  sortBy: SortBy;
}

export function AprSortHeader({
  labelContent,
  labelRef,
  labelWidth,
  depositField,
  debtField,
  marketsSort,
  sortBy,
}: AprSortHeaderProps): JSX.Element {
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="flex flex-wrap justify-end text-right">
        <span style={{ width: labelWidth }}>
          <span ref={labelRef} className="inline-block whitespace-nowrap">
            {labelContent} APR:&nbsp;
          </span>
        </span>
        <span className="whitespace-nowrap">
          <AprSortButton
            marketsSort={marketsSort}
            sortBy={sortBy}
            field={depositField}
          >
            Deposit
          </AprSortButton>{" "}
          /{" "}
          <AprSortButton
            marketsSort={marketsSort}
            sortBy={sortBy}
            field={debtField}
          >
            Borrow
          </AprSortButton>
        </span>
      </div>

      <AprSortIcon
        marketsSort={marketsSort}
        sortBy={sortBy}
        depositField={depositField}
        debtField={debtField}
      />
    </div>
  );
}

interface AprSortButtonProps {
  field: MarketsSortField;
  marketsSort: MarketsSort;
  sortBy: SortBy;
  children?: ReactNode;
}

function AprSortButton({
  field,
  marketsSort,
  sortBy,
  children,
}: AprSortButtonProps): JSX.Element {
  return (
    <button
      className={sortButton({
        isActive: marketsSort?.field === field,
      })}
      onClick={() => {
        sortBy(field);
      }}
    >
      {children}
    </button>
  );
}

interface AprSortIconProps {
  depositField: MarketsSortField;
  debtField: MarketsSortField;
  marketsSort: MarketsSort;
  sortBy: SortBy;
}

function AprSortIcon({
  depositField,
  debtField,
  marketsSort,
  sortBy,
}: AprSortIconProps): JSX.Element {
  const onClick = () => {
    if (marketsSort?.field === debtField) {
      sortBy(debtField);
    } else {
      sortBy(depositField);
    }
  };

  const isEitherFieldSelected =
    marketsSort?.field === depositField || marketsSort?.field === debtField;

  return (
    <button
      className={sortButton({
        isActive: isEitherFieldSelected,
      })}
      onClick={onClick}
    >
      {match(marketsSort)
        .with(
          { field: P.union(depositField, debtField), direction: "desc" },
          () => <ArrowDownIcon className="w-4 h-4" />
        )
        .with(
          { field: P.union(depositField, debtField), direction: "asc" },
          () => <ArrowUpIcon className="w-4 h-4" />
        )
        .otherwise(() => (
          <ArrowUpDownIcon className="w-4 h-4" />
        ))}
    </button>
  );
}
